import axios from "axios";

axios.defaults.withCredentials = true;

export const axiosService = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Allow the browser to set the correct Content-Type (with boundary) for FormData
axiosService.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

let isRefreshing = false;
let refreshSubscribers = [];

const onTokenRefreshed = () => {
  refreshSubscribers.forEach((cb) => cb());
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

axiosService.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/logout') ||
      originalRequest.url?.includes('/auth/refresh')
    ) {
      return Promise.reject(error);
    }

    // Don't redirect on public pages (match with or without trailing slash)
    const isPublicPage = ['/login', '/register', '/auth/callback', '/reset-password', '/p', '/labs', '/blog'].some(
      (route) => window.location.pathname === route || window.location.pathname.startsWith(route + '/')
    );

    if (error.response?.status === 429) {
      return Promise.reject(error);
    }

    // A blocked account (e.g. after repeated wrong merge codes) can only see the block page
    if (error.response?.status === 403 && error.response?.data?.code === 'account_blocked'
        && !window.location.pathname.startsWith('/account-blocked')) {
      window.location.href = '/account-blocked/';
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber(() => {
            axiosService(originalRequest).then(resolve).catch(reject);
          });
        });
      }

      isRefreshing = true;

      try {
        await axiosService.post("/auth/refresh/");
        onTokenRefreshed();
        isRefreshing = false;
        return axiosService(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        localStorage.removeItem("user");
        if (!isPublicPage) {
          window.location.href = "/login/";
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export function fetcher(url) {
  return axiosService.get(url).then((res) => res.data);
}

// DEPRECATED: use useUserContext() from 'shared/contexts/UserContext' instead
export const useUser = () => {
  const { useUserContext } = require('shared/contexts/UserContext');
  const { user, loading, isAuthenticated, refreshUser } = useUserContext();
  return {
    user,
    isLoading: loading,
    isAuthenticated,
    isAdmin: user?.role === 'admin',
    refreshUserData: refreshUser,
  };
};

const axiosHelpers = { axiosService, fetcher, useUser };
export default axiosHelpers;
